/**
 * Modules Service - API calls for modules management
 *
 * Simplifié : les modules actifs sont maintenant gérés via PreferencesContext
 * Ce service ne gère que les appels API pour les modules et groupes
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be';

// LocalStorage key for all modules list
const MODULES_LIST_KEY = 'cabazaModulesList';

/**
 * Get all active modules from API
 * @param {string} lang - Language code (fr, en, etc.)
 * @returns {Promise<Array>} List of modules
 */
export const getModules = async (lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/modules?lang=${lang}&isActive=1`);
        if (!response.ok) throw new Error('Failed to fetch modules');
        return await response.json();
    } catch (error) {
        console.error('Error fetching modules:', error);
        return [];
    }
};

/**
 * Get modules list from localStorage (loaded at login)
 * @returns {Array} List of all modules
 */
export const getModulesList = () => {
    try {
        const stored = localStorage.getItem(MODULES_LIST_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error('Error reading modules from localStorage:', e);
    }
    return [];
};

/**
 * Save modules list to localStorage
 * @param {Array} modules - List of modules
 */
export const setModulesList = (modules) => {
    try {
        localStorage.setItem(MODULES_LIST_KEY, JSON.stringify(modules || []));
    } catch (e) {
        console.error('Error saving modules to localStorage:', e);
    }
};

/**
 * Get module by code
 * @param {string} code - Module code (e.g., 'CONTACTS')
 * @returns {Object|null} Module data
 */
export const getModuleByCode = (code) => {
    const modules = getModulesList();
    return modules.find(m => m.code === code || m.CODE === code) || null;
};

/**
 * Get module by navId
 * @param {string} navId - Navigation ID (e.g., 'contacts')
 * @returns {Object|null} Module data
 */
export const getModuleByNavId = (navId) => {
    const modules = getModulesList();
    return modules.find(m => {
        const moduleNavId = (m.navid || m.NAVID || m.code || m.CODE || '').toLowerCase();
        return moduleNavId === navId;
    }) || null;
};

// Static maps for fallback and backward compatibility
export const MODULE_NAV_MAP = {
    'CONTACTS': 'contacts',
    'MESSAGES': 'messages',
    'MAP': 'map',
    'AGENDA': 'calendar',
    'EVENTS': 'events',
    'PLACES': 'places',
    'SUPPORT': 'support',
    'WORK': 'work',
    'OFFERS': 'offers',
    'ADVERTISEMENTS': 'advertisements',
    'WEBSITES': 'websites',
    'PRODUCTS': 'products',
    'KNOWLEDGE': 'knowledge',
    'EXPENSES': 'expenses',
    'BOOKS': 'books',
    'MOMENTS': 'moments',
    'CARPOOLING': 'carpooling',
    'OCCUPATION_GROUPS': 'occupationGroups',
    'PROJECTS': 'projects',
    'TIME_TRACKING': 'timeTracking',
    'MEETINGS': 'meetings',
    'NEIGHBORHOOD': 'neighborhood'
};

export const NAV_TO_MODULE_MAP = {
    'contacts': 'CONTACTS',
    'messages': 'MESSAGES',
    'map': 'MAP',
    'calendar': 'AGENDA',
    'events': 'EVENTS',
    'places': 'PLACES',
    'support': 'SUPPORT',
    'work': 'WORK',
    'offers': 'OFFERS',
    'advertisements': 'ADVERTISEMENTS',
    'websites': 'WEBSITES',
    'products': 'PRODUCTS',
    'knowledge': 'KNOWLEDGE',
    'expenses': 'EXPENSES',
    'books': 'BOOKS',
    'moments': 'MOMENTS',
    'carpooling': 'CARPOOLING',
    'occupationGroups': 'OCCUPATION_GROUPS',
    'projects': 'PROJECTS',
    'timeTracking': 'TIME_TRACKING',
    'meetings': 'MEETINGS',
    'neighborhood': 'NEIGHBORHOOD'
};

// Helper to convert module code to navId
const codeToNavId = (code) => {
    const specialCases = {
        'MESSAGES': 'messages',
        'AGENDA': 'calendar',
        'OCCUPATION_GROUPS': 'occupationGroups',
        'PROJECTS': 'projects',
        'TIME_TRACKING': 'timeTracking'
    };
    return specialCases[code] || code?.toLowerCase();
};

/**
 * Build MODULE_NAV_MAP dynamically from localStorage
 */
export const getModuleNavMap = () => {
    const modules = getModulesList();
    if (!modules || modules.length === 0) {
        return MODULE_NAV_MAP;
    }
    const map = {};
    modules.forEach(m => {
        const code = (m.code || m.CODE || '').toUpperCase();
        const navId = m.navid || m.NAVID || codeToNavId(code);
        if (code && navId) {
            map[code] = navId;
        }
    });
    return map;
};

/**
 * Build NAV_TO_MODULE_MAP dynamically from localStorage
 */
export const getNavToModuleMap = () => {
    const modules = getModulesList();
    if (!modules || modules.length === 0) {
        return NAV_TO_MODULE_MAP;
    }
    const map = {};
    modules.forEach(m => {
        const code = (m.code || m.CODE || '').toUpperCase();
        const navId = m.navid || m.NAVID || codeToNavId(code);
        if (code && navId) {
            map[navId] = code;
        }
    });
    return map;
};

/**
 * Build MODULE_ID_MAP dynamically from localStorage
 */
export const getModuleIdMap = () => {
    const modules = getModulesList();
    const map = {};
    modules.forEach(m => {
        const code = m.code || m.CODE;
        const id = m.id || m.ID;
        if (code && id) {
            map[code] = id;
        }
    });
    return map;
};

/**
 * Build MODULE_CODE_MAP dynamically from localStorage
 */
export const getModuleCodeMap = () => {
    const modules = getModulesList();
    const map = {};
    modules.forEach(m => {
        const code = m.code || m.CODE;
        const id = m.id || m.ID;
        if (code && id) {
            map[id] = code;
        }
    });
    return map;
};

// ============================================
// API calls for module groups (server-side)
// ============================================

/**
 * Get user's module groups from API
 * @param {number} peopleId - User ID
 * @returns {Promise<Array>} List of groups
 */
export const getPeopleGroups = async (peopleId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/modules/people/${peopleId}/groups`);
        if (!response.ok) throw new Error('Failed to fetch groups');
        return await response.json();
    } catch (error) {
        console.error('Error fetching groups:', error);
        return [];
    }
};

/**
 * Create a new module group
 * @param {number} peopleId - User ID
 * @param {string} name - Group name (in user's current language)
 * @param {string} sourceLang - Language code of the name (fr, en, es, etc.)
 * @param {string} color - Optional color
 * @returns {Promise<Object>} Result with success status, groupId, and translations
 */
export const createModuleGroup = async (peopleId, name, sourceLang = 'fr', color = '') => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/modules/groups`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ peopleId, name, sourceLang, color })
        });
        return await response.json();
    } catch (error) {
        console.error('Error creating group:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update a module group
 * @param {number} groupId - Group ID
 * @param {Object} data - Update data (name, sortOrder, isCollapsed, color)
 * @returns {Promise<Object>} Result with success status
 */
export const updateModuleGroup = async (groupId, data) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/modules/groups/${groupId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        return await response.json();
    } catch (error) {
        console.error('Error updating group:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a module group
 * @param {number} groupId - Group ID
 * @returns {Promise<Object>} Result with success status
 */
export const deleteModuleGroup = async (groupId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/modules/groups/${groupId}`, {
            method: 'DELETE'
        });
        return await response.json();
    } catch (error) {
        console.error('Error deleting group:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Add a module to a group
 * @param {number} groupId - Group ID
 * @param {number} moduleId - Module ID
 * @returns {Promise<Object>} Result with success status and itemId
 */
export const addModuleToGroup = async (groupId, moduleId) => {
    try {
        const numericGroupId = parseInt(groupId, 10);
        const numericModuleId = parseInt(moduleId, 10);

        const response = await fetch(`${API_BASE}/rest/cabaza/modules/groups/${numericGroupId}/modules`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ moduleId: numericModuleId })
        });
        return await response.json();
    } catch (error) {
        console.error('Error adding module to group:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Remove a module from a group
 * @param {number} groupId - Group ID
 * @param {number} moduleId - Module ID
 * @returns {Promise<Object>} Result with success status
 */
export const removeModuleFromGroup = async (groupId, moduleId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/modules/groups/${groupId}/modules/${moduleId}`, {
            method: 'DELETE'
        });
        return await response.json();
    } catch (error) {
        console.error('Error removing module from group:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Reorder modules in a group
 * @param {number} groupId - Group ID
 * @param {Array<number>} moduleIds - Array of module IDs in new order
 * @returns {Promise<Object>} Result with success status
 */
export const reorderGroupModules = async (groupId, moduleIds) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/modules/groups/${groupId}/reorder`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ moduleIds })
        });
        return await response.json();
    } catch (error) {
        console.error('Error reordering modules:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get modules in a specific group
 * @param {number} groupId - Group ID
 * @returns {Promise<Array>} List of modules in the group
 */
export const getGroupModules = async (groupId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/modules/groups/${groupId}/modules`);
        if (!response.ok) throw new Error('Failed to fetch group modules');
        return await response.json();
    } catch (error) {
        console.error('Error fetching group modules:', error);
        return [];
    }
};

/**
 * Toggle module pinned status in a group
 * @param {number} groupId - Group ID
 * @param {number} moduleId - Module ID
 * @param {boolean} isPinned - New pinned status
 * @returns {Promise<Object>} Result with success status
 */
export const toggleModulePinned = async (groupId, moduleId, isPinned) => {
    try {
        const response = await fetch(`${API_BASE}/rest/cabaza/modules/groups/${groupId}/modules/${moduleId}/pinned`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isPinned })
        });
        return await response.json();
    } catch (error) {
        console.error('Error toggling module pinned status:', error);
        return { success: false, message: error.message };
    }
};
